import React from "react";
import {inject, observer} from "mobx-react";
import {withRouter} from "react-router-dom";
import configuration from "../../configuration/configuration";
import {resoudre} from "./fonds/reglages";
import {champsModifies, fiche, ficheVide, groupes, morceauxCoupables, prenomAAccueillir, tailleFiche} from "./champsClient";
import {ID_IMAGE_FOND, ID_IMAGE_GANT, ID_REGLAGES, REGLAGES_SAISIE, premiereImage} from "./saisie/reglagesSaisie";
import {themeOuDefaut} from "./saisie/themes";

/** La main-gant qui pointe un champ (dessinée ici : manchette, poing et index tendu). */
const Gant = () => (
    <svg viewBox="0 0 48 30" aria-hidden="true">
        <rect x="14" y="10" width="33" height="9" rx="4.5" fill="#fff" stroke="#2a2f6e" strokeWidth="1.6"/>
        <rect x="7" y="5" width="20" height="21" rx="8" fill="#fff" stroke="#2a2f6e" strokeWidth="1.6"/>
        <rect x="1" y="8" width="8" height="15" rx="2" fill="#7f8ae0" stroke="#2a2f6e" strokeWidth="1.6"/>
        <path d="M14 21h10M14 16h10" stroke="#b7bde6" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
);

/** Dimensions de l'écran de la boutique (vertical Full HD, bandeau de 112 px en haut) : servent à l'aperçu du tableau de bord. */
export const ECRAN = {largeur: 1080, hauteur: 1920 - 112};

/**
 * Écran client, mode « Client » : la fiche que la Caisse est en train de saisir s'affiche en direct, pour que le client vérifie
 * qu'il n'y a aucune faute. Chaque valeur est rangée sous son libellé, dans des rubriques lisibles de loin ; le champ qui vient d'être
 * modifié passe à l'or un instant et un curseur (une main-gant, ou l'image choisie) le pointe ; un champ encore vide reste visible
 * (« à renseigner »). Le calcul est dans champsClient.js (testé).
 *
 * L'habillage vient des réglages du tableau de bord (carte « Réglage saisi client », voir saisie/reglagesSaisie.js) : style (saisie/themes.js),
 * image de fond, image du curseur, assombrissement, taille du curseur, message du bas.
 *
 * @param datas          ce que la Caisse envoie : une fiche nommée {nom, prenom, email, ...}, ou l'ancienne liste de valeurs
 * @param apercu         (tableau de bord) dessine l'écran à sa taille réelle, sans dépendre de la fenêtre, pour être réduit en aperçu
 * @param reglagesSaisie (aperçu) valeurs de réglage imposées, à la place de celles enregistrées
 * @param imageFond      (aperçu) adresse de l'image de fond imposée (null : aucune) ; non fournie : celle enregistrée
 * @param imageGant      (aperçu) adresse de l'image du curseur imposée (null : aucune) ; non fournie : celle enregistrée
 * @param curseurSur     (aperçu) champ pointé dès l'affichage (sinon, le champ modifié en dernier)
 */
const ClientModeComponent = (inject("rootStore")(observer(class ClientModeComponent extends React.Component {

    state = {
        // nombre de modifications de chaque champ : change la clé de son affichage, donc relance l'animation « modifié »
        versions: {},
        // champ modifié en dernier : c'est lui que le curseur pointe (le client voit où la Caisse est en train de saisir)
        dernier: this.props.curseurSur || null,
    };

    componentDidUpdate(precedent) {
        if (precedent.datas === this.props.datas) return;
        const modifies = champsModifies(precedent.datas, this.props.datas);
        if (modifies.length === 0) return;
        this.setState(({versions}) => {
            const suivantes = {...versions};
            modifies.forEach(cle => { suivantes[cle] = (suivantes[cle] || 0) + 1; });
            return {versions: suivantes, dernier: modifies[modifies.length - 1]};
        });
    }

    /** Réglages, image de fond et image du curseur : ceux imposés (aperçu) ou, à défaut, ceux enregistrés. */
    parametres() {
        const auth = this.props.rootStore.authStore;
        const url = image => (image ? configuration.API + image.url : null);
        const reglages = resoudre(REGLAGES_SAISIE, this.props.reglagesSaisie !== undefined ? this.props.reglagesSaisie : auth.fondReglages[ID_REGLAGES]);
        const fond = this.props.imageFond !== undefined ? this.props.imageFond : url(premiereImage(auth.fondImages[ID_IMAGE_FOND]));
        const gant = this.props.imageGant !== undefined ? this.props.imageGant : url(premiereImage(auth.fondImages[ID_IMAGE_GANT]));
        return {reglages, fond, gant};
    }

    render() {
        const {datas, apercu} = this.props;
        const {versions, dernier} = this.state;
        const {reglages, fond, gant} = this.parametres();
        const theme = themeOuDefaut(reglages.theme);
        const styles = theme.styles;
        const {champs, liste} = fiche(datas);
        const prenom = prenomAAccueillir(datas);

        const style = {"--gant": reglages.gant / 100};
        if (apercu) {
            // à sa taille réelle, indépendamment de la fenêtre du tableau de bord (l'unité --u vaut 1 % de la largeur de l'écran)
            style["--u"] = `${ECRAN.largeur / 100}px`;
            style.width = ECRAN.largeur;
            style.minHeight = ECRAN.hauteur;
        }
        if (fond) {
            // l'image remplace le fond sombre ; un voile noir (réglable) la rend compatible avec le texte
            const voile = reglages.assombrissement / 100;
            style.backgroundImage = `linear-gradient(rgba(0, 0, 0, ${voile}), rgba(0, 0, 0, ${voile})), url("${fond}")`;
            style.backgroundSize = "cover";
            style.backgroundPosition = "center";
        }

        const curseur = (
            <span className={styles.curseur} aria-hidden="true">
                {gant
                    ? <img src={gant} alt=""/>
                    : theme.curseur === "gant" ? <Gant/> : <span className={styles.triangle}>▶</span>}
            </span>
        );

        return (
            <div className={`${styles.scene} ${styles[tailleFiche(champs)] || ""}`} style={style}>
                <header className={`${styles.fenetre} ${styles.entete}`}>
                    <h1 className={styles.titre}>{prenom ? `Bonjour ${prenom}` : "Bonjour"}</h1>
                </header>

                {ficheVide(datas) && champs.length === 0 && (
                    <div className={styles.attente} role="status">
                        <span className={styles.point}/>Votre fiche s'affiche ici au fur et à mesure de la saisie
                    </div>
                )}

                {liste && (
                    <div className={styles.carte}>
                        {champs.map(c => <div key={c.cle + (versions[c.cle] || 0)} className={`${styles.ligne} ${styles.modifie}`}>{c.valeur}</div>)}
                    </div>
                )}

                {!liste && groupes(champs).map(g => (
                    <section key={g.id} className={styles.carte} aria-label={g.titre}>
                        <h2 className={styles.rubrique}>{g.titre}</h2>
                        <dl className={styles.champs}>
                            {g.champs.map(c => (
                                <div key={c.cle} className={`${styles.champ} ${c.large ? styles.large : ""}`}>
                                    {dernier === c.cle && curseur}
                                    <dt className={styles.libelle}>{c.libelle}</dt>
                                    {/* la clé change à chaque modification : l'animation repart */}
                                    <dd
                                        key={c.cle + "-" + (versions[c.cle] || 0)}
                                        className={`${styles.valeur} ${c.vide ? styles.vide : ""} ${versions[c.cle] ? styles.modifie : ""} ${c.type === "email" ? styles.courrier : ""} ${c.type === "telephone" ? styles.numero : ""}`}
                                    >
                                        {c.vide ? "à renseigner" : c.type === "email"
                                            ? morceauxCoupables(c.valeur).map((m, i) => <React.Fragment key={i}>{m}<wbr/></React.Fragment>)
                                            : c.valeur}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </section>
                ))}

                {reglages.dialogue && (
                    <footer className={styles.dialogue}>
                        <p>Merci de vérifier vos informations. Dites-nous s'il y a la moindre faute.</p>
                        <span className={styles.suite} aria-hidden="true">▼</span>
                    </footer>
                )}
            </div>
        );
    }
})));

export default withRouter(ClientModeComponent);
